import { DT, GRAVITY, KMH } from "./constants";
import {
  ASSISTS_ON,
  type AssistSettings,
  type AxleGrip,
  type CarParams,
  type TorquePoint,
} from "./types";
import { brakeOf, steerOf, throttleOf, type InputFrame } from "./input";
import {
  DEG,
  TWO_PI,
  atan2,
  clamp,
  clamp01,
  cos,
  lerp,
  sign,
  sin,
  smoothstep,
  sqrt,
  wrapAngle,
} from "./math";
import { createTrackSample, sampleTrack, trackPointAt, type TrackModel } from "./track";
import { resolveWalls } from "./wall";

/** Everything the simulation remembers between ticks. Plain numbers so it copies and hashes cheaply. */
export interface VehicleState {
  tick: number;
  /** Ticks on the run clock; stops when the finish line is crossed. */
  runTicks: number;
  finished: boolean;
  finishTick: number;

  // Pose and velocity. Velocities are in the body frame (x forward, y left).
  x: number;
  y: number;
  z: number;
  yaw: number;
  vx: number;
  vy: number;
  yawRate: number;

  // Controls after assists and smoothing.
  steerAngle: number;
  throttle: number;
  handbrake: number;

  // Drivetrain.
  gear: number;
  rpm: number;
  shiftTicks: number;
  /** True while the brake is acting as reverse. */
  reversing: boolean;

  /** Smoothed longitudinal acceleration, m/s², drives weight transfer. */
  accelX: number;
  slipFront: number;
  slipRear: number;

  // Track position (from the last tick).
  trackIndex: number;
  distance: number;
  lateral: number;

  // Walls.
  wallContact: boolean;
  lastContactTick: number;
  scrapes: number;
  crashes: number;
  /** Ticks left in the post-crash hold. */
  lockTicks: number;
}

export function createVehicleState(
  track: TrackModel,
  distance = track.startDistance,
): VehicleState {
  const s: VehicleState = {
    tick: 0,
    runTicks: 0,
    finished: false,
    finishTick: 0,
    x: 0,
    y: 0,
    z: 0,
    yaw: 0,
    vx: 0,
    vy: 0,
    yawRate: 0,
    steerAngle: 0,
    throttle: 0,
    handbrake: 0,
    gear: 1,
    rpm: 0,
    shiftTicks: 0,
    reversing: false,
    accelX: 0,
    slipFront: 0,
    slipRear: 0,
    trackIndex: 0,
    distance: 0,
    lateral: 0,
    wallContact: false,
    lastContactTick: -1000,
    scrapes: 0,
    crashes: 0,
    lockTicks: 0,
  };
  placeOnTrack(s, track, distance);
  return s;
}

export function copyVehicleState(dst: VehicleState, src: VehicleState): void {
  Object.assign(dst, src);
}

const scratch = createTrackSample();

/** Put the car on the centre-line at `distance`, facing forward and stationary. */
export function placeOnTrack(s: VehicleState, track: TrackModel, distance: number): void {
  const p = trackPointAt(track, distance, scratch);
  s.x = p.x;
  s.y = p.y;
  s.z = p.z;
  s.yaw = atan2(p.tx, p.tz);
  s.vx = 0;
  s.vy = 0;
  s.yawRate = 0;
  s.steerAngle = 0;
  s.throttle = 0;
  s.handbrake = 0;
  s.gear = 1;
  s.rpm = 0;
  s.shiftTicks = 0;
  s.reversing = false;
  s.accelX = 0;
  s.slipFront = 0;
  s.slipRear = 0;
  s.trackIndex = p.index;
  s.distance = p.distance;
  s.lateral = 0;
  s.wallContact = false;
}

/** Manual reset (R key): back to the centre-line where the car is now, facing forward. */
export function resetToTrack(s: VehicleState, track: TrackModel): void {
  placeOnTrack(s, track, s.distance);
}

/** Crash reset: as `resetToTrack`, plus the crash count and a short hold. */
export function crashReset(s: VehicleState, track: TrackModel): void {
  placeOnTrack(s, track, s.distance);
  s.crashes += 1;
  s.lockTicks = track.tuning.wall.crashLockTicks;
}

/** Linear interpolation over the torque curve, clamped at both ends. */
export function torqueAt(curve: readonly TorquePoint[], rpm: number): number {
  const first = curve[0]!;
  if (rpm <= first.rpm) return first.torque;
  for (let i = 1; i < curve.length; i++) {
    const b = curve[i]!;
    if (rpm <= b.rpm) {
      const a = curve[i - 1]!;
      return lerp(a.torque, b.torque, (rpm - a.rpm) / (b.rpm - a.rpm));
    }
  }
  return curve[curve.length - 1]!.torque;
}

/** rpm per (m/s) of forward speed in a gear. */
function rpmPerSpeed(car: CarParams, gear: number): number {
  const ratio = car.gearRatios[gear - 1]! * car.finalDrive;
  return (ratio / car.wheelRadius) * (60 / TWO_PI);
}

// Outputs of `axleForce`, kept module-level to avoid allocating every tick.
let axleFx = 0;
let axleFy = 0;

/**
 * Tyre force for one axle. The requested longitudinal force is limited by grip, and what's left of
 * the friction circle is available for lateral force, which follows a "linear up to the peak, then
 * fall off" curve of slip angle (a Pacejka-lite).
 */
function axleForce(
  grip: AxleGrip,
  muScale: number,
  load: number,
  fxRequest: number,
  slip: number,
  combined: number,
) {
  const fMax = grip.mu * muScale * load;
  const fx = clamp(fxRequest, -fMax, fMax);
  const used = (fx * combined) / fMax;
  const fyMax = fMax * sqrt(Math.max(0, 1 - used * used));
  const x = Math.abs(slip) / (grip.peakSlipDeg * DEG);
  const curve = x <= 1 ? x : 1 - (1 - grip.falloff) * smoothstep((x - 1) / grip.falloffSpan);
  axleFx = fx;
  axleFy = -sign(slip) * fyMax * curve;
}

/** Advance the car by one fixed tick. Mutates `s`. */
export function step(
  s: VehicleState,
  input: InputFrame,
  car: CarParams,
  track: TrackModel,
  assists: AssistSettings = ASSISTS_ON,
): void {
  s.tick += 1;
  if (!s.finished) s.runTicks += 1;

  if (s.lockTicks > 0) {
    s.lockTicks -= 1;
    return;
  }

  const tune = track.tuning;
  const m = car.mass;
  const wheelbase = car.wheelbase;
  const a = wheelbase * (1 - car.frontWeight); // CoG to front axle
  const b = wheelbase * car.frontWeight; // CoG to rear axle
  const inertia = m * car.yawInertiaFactor * wheelbase * wheelbase;
  const topSpeed = car.topSpeedKmh / KMH;

  const { vx, vy, yawRate } = s;
  const speed = sqrt(vx * vx + vy * vy);
  const forward = vx > 0;

  // ---- Driver inputs ----
  const steerIn = steerOf(input);
  const throttleIn = throttleOf(input);
  const brakeIn = brakeOf(input);

  // Throttle smoothing: rises are rate-limited by the assist; lifting is instant.
  const smoothing = clamp01(assists.throttleSmoothing);
  const maxRise = 1 - smoothing + smoothing * car.assists.throttleSlew * DT;
  s.throttle = throttleIn > s.throttle ? Math.min(throttleIn, s.throttle + maxRise) : throttleIn;

  // Handbrake eases on and off so it feels like a lever, not a switch.
  const hbTarget = input.handbrake ? 1 : 0;
  const hbRate = hbTarget > s.handbrake ? car.handbrake.attack : car.handbrake.release;
  s.handbrake += (hbTarget - s.handbrake) * hbRate;
  if (s.handbrake < 0.001) s.handbrake = 0;

  // ---- Steering ----
  // Lock shrinks quickly with speed (front-loaded curve) so small inputs stay gentle, then reopens
  // towards full lock as the car slides, so there is always enough to counter-steer with.
  const lock = car.steering.lockDeg * DEG;
  const speedFrac = Math.min(speed / topSpeed, 1);
  const lockFast = lock * (1 - (1 - car.steering.highSpeedFactor) * speedFrac * (2 - speedFrac));
  const beta = forward ? atan2(vy, Math.max(vx, tune.lowSpeed)) : 0; // body slip at the CoG
  const slide = clamp01(Math.abs(beta) / (car.steering.slideOpenDeg * DEG));
  const lockNow = lockFast + (lock - lockFast) * slide;
  // InputFrame steer is right-positive; the body frame is left-positive.
  let delta = -steerIn * lockNow;

  const counterSteer = car.assists.counterSteer * clamp01(assists.counterSteer);
  if (counterSteer > 0 && vx > tune.lowSpeed) {
    // Blend the wheels towards the direction the front axle is actually travelling, which is the
    // counter-steer angle. Only ever adds to the driver's own counter-steer.
    const ramp = clamp01(
      (Math.abs(beta) / DEG - car.assists.counterSteerStartDeg) / car.assists.counterSteerRangeDeg,
    );
    const target = clamp(atan2(vy + a * yawRate, vx), -lockNow, lockNow);
    const blend = counterSteer * ramp;
    const blended = delta + (target - delta) * blend;
    const sameSide = delta * target > 0;
    delta = sameSide && Math.abs(delta) > Math.abs(blended) ? delta : blended;
  }
  s.steerAngle = delta;

  // ---- Gearbox ----
  // Wheel speed follows total speed, not just the forward part, so a sliding car keeps its revs up.
  const speedFwd = forward ? speed : 0;
  const rpmScale = rpmPerSpeed(car, s.gear);
  let rpm = Math.max(speedFwd * rpmScale, car.idleRpm);
  if (s.gear === 1 && s.throttle > 0) rpm = Math.max(rpm, car.launchRpm * s.throttle);
  s.rpm = Math.min(rpm, car.redlineRpm);

  if (s.shiftTicks > 0) {
    s.shiftTicks -= 1;
  } else if (s.gear < car.gearRatios.length && rpm > car.upshiftRpm && forward) {
    s.gear += 1;
    s.shiftTicks = car.shiftTicks;
  } else if (s.gear > 1) {
    const lowerRpm = speedFwd * rpmPerSpeed(car, s.gear - 1);
    if (rpm < car.downshiftRpm && lowerRpm < car.upshiftRpm * 0.92) {
      s.gear -= 1;
      s.shiftTicks = car.shiftTicks;
    }
  }

  // ---- Longitudinal demand ----
  const gearRatio = car.gearRatios[s.gear - 1]! * car.finalDrive;
  const revCut = rpm >= car.redlineRpm ? 0 : 1;
  const limiter = clamp01((topSpeed - speed) / (topSpeed * 0.04));
  const engineTorque = torqueAt(car.torqueCurve, s.rpm) * s.throttle * revCut;
  let driveForce =
    s.shiftTicks > 0
      ? 0
      : ((engineTorque * gearRatio * car.drivetrainEfficiency) / car.wheelRadius) * limiter;
  // Engine braking when off the throttle.
  if (vx > 0.5) {
    driveForce -= m * car.engineBraking * (s.rpm / car.redlineRpm) * (1 - s.throttle);
  }

  // Braking (and the handbrake) can stop the car but never push it backwards. Once the car is
  // (nearly) stopped, holding the brake with no throttle becomes reverse.
  const reversing = brakeIn > 0 && throttleIn === 0 && vx < car.reverse.engageSpeed;
  s.reversing = reversing;
  const stopForce = (m * Math.max(vx, 0)) / DT;
  const brakeForce = reversing ? 0 : Math.min(brakeIn * car.brakeDecel * m, stopForce);
  const handbrakeForce = Math.min(s.handbrake * car.handbrake.brakeDecel * m, stopForce);
  if (reversing) {
    const top = car.reverse.topSpeedKmh / KMH;
    const fade = clamp01((top + vx) / (top * 0.1));
    driveForce = -brakeIn * car.reverse.accel * m * fade;
  }

  const frontShare = car.drivetrain === "AWD" ? car.awdFrontSplit : 0;
  const fxFrontRequest = driveForce * frontShare - brakeForce * car.brakeBias;
  const fxRearRequest =
    driveForce * (1 - frontShare) - brakeForce * (1 - car.brakeBias) - handbrakeForce;

  // ---- Axle loads with weight transfer ----
  const weight = m * GRAVITY;
  const transfer = clamp(
    (m * s.accelX * car.cogHeight * car.weightTransferGain) / wheelbase,
    -0.3 * weight,
    0.3 * weight,
  );
  const loadFront = car.frontWeight * weight - transfer;
  const loadRear = (1 - car.frontWeight) * weight + transfer;

  // ---- Slip angles ----
  const vxEff = Math.max(Math.abs(vx), tune.lowSpeed);
  const frontLat = vy + a * yawRate;
  let slipFront: number;
  if (forward) {
    slipFront = atan2(frontLat, vxEff) - delta;
  } else {
    // Backing up: measure velocity in the steered wheel's own frame so steering still works.
    const cosS = cos(delta);
    const sinS = sin(delta);
    const lat = frontLat * cosS - vx * sinS;
    const lon = vx * cosS + frontLat * sinS;
    slipFront = atan2(lat, Math.max(Math.abs(lon), tune.lowSpeed));
  }
  const slipRear = atan2(vy - b * yawRate, vxEff);
  s.slipFront = slipFront;
  s.slipRear = slipRear;

  // ---- Grip modifiers ----
  const surface = clamp(sampleGrip(track, s), 0.1, 2);
  const rearMul =
    lerp(1, car.handbrake.rearGrip, s.handbrake) * angleCapBoost(car, assists, beta, forward);

  // ---- Tyre forces ----
  axleForce(car.gripFront, surface, loadFront, fxFrontRequest, slipFront, car.combinedSlip);
  const fxF = axleFx;
  const fyF = axleFy;
  axleForce(car.gripRear, surface * rearMul, loadRear, fxRearRequest, slipRear, car.combinedSlip);
  const fxR = axleFx;
  const fyR = axleFy;

  const cosD = cos(delta);
  const sinD = sin(delta);
  const fxFrontBody = fxF * cosD - fyF * sinD;
  const fyFrontBody = fxF * sinD + fyF * cosD;

  // Drag and rolling resistance oppose motion along both axes.
  const resist = car.rollingResistance + car.dragCoeff * speed;
  const fxBody = fxFrontBody + fxR - resist * vx;
  const fyBody = fyFrontBody + fyR - resist * vy;
  const mz = a * fyFrontBody - b * fyR;

  // ---- Integrate (semi-implicit Euler in the body frame) ----
  s.accelX += (fxBody / m - s.accelX) * 0.25;
  let nvx = vx + (fxBody / m + vy * yawRate) * DT;
  const nvy = vy + (fyBody / m - vx * yawRate) * DT;
  if (nvx < 0 && !reversing) nvx = Math.min(0, nvx + car.backwardDecel * DT);
  s.vx = nvx;
  s.vy = nvy;
  s.yawRate = yawRate + (mz / inertia - car.yawDamping * yawRate) * DT;

  s.yaw = wrapAngle(s.yaw + s.yawRate * DT);
  const sy = sin(s.yaw);
  const cy = cos(s.yaw);
  // Forward is (sin yaw, cos yaw); left is (cos yaw, -sin yaw).
  s.x += (s.vx * sy + s.vy * cy) * DT;
  s.z += (s.vx * cy - s.vy * sy) * DT;

  // ---- Track position and walls ----
  const p = sampleTrack(track, s.x, s.z, s.trackIndex, scratch);
  s.trackIndex = p.index;
  s.distance = p.distance;
  s.lateral = p.lateral;
  s.y = p.y;
  resolveWalls(s, car, track, p);

  if (!s.finished && s.distance >= track.finishDistance) {
    s.finished = true;
    s.finishTick = s.runTicks;
  }
}

/** Grip multiplier of the surface under the car, from the previous tick's track sample. */
function sampleGrip(track: TrackModel, s: VehicleState): number {
  const surfaceName = track.surfaceNames[track.surface[s.trackIndex]!]!;
  return track.tuning.surfaces[surfaceName];
}

/** The drift angle cap: grow rear grip as body slip passes the cap, so the car resists spinning. */
function angleCapBoost(
  car: CarParams,
  assists: AssistSettings,
  beta: number,
  forward: boolean,
): number {
  const strength = clamp01(assists.angleCap);
  if (strength === 0 || !forward) return 1;
  const over = Math.abs(beta) / DEG - car.assists.angleCapDeg;
  if (over <= 0) return 1;
  return 1 + strength * car.assists.angleCapGain * Math.min(over / 20, 2);
}

/** Body slip angle at the CoG in radians (the drift angle). */
export function driftAngle(s: VehicleState): number {
  return s.vx > 0.5 ? atan2(s.vy, s.vx) : 0;
}

/** Speed in m/s. */
export function speedOf(s: VehicleState): number {
  return sqrt(s.vx * s.vx + s.vy * s.vy);
}
